import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Close, DeleteOutline } from "@mui/icons-material";
import { useTheme } from "../../context/ThemeContext";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import composioService from "../../services/composioService";
import styles from "./LiveSourcesDialog.module.scss";

/**
 * A "live source" lets the agent look things up in a connected app in real time
 * (Drive, Gmail, …) instead of the user uploading files.
 *
 * Distinct from tools: tools let the agent *do* things in an app, live sources
 * let it *look things up*. The same app can be both.
 */
const LiveSourcesDialog = ({ open, onClose, onSave, initialProviders }) => {
  const { isDarkMode } = useTheme();

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: { main: "#e36250" },
      secondary: { main: "#8b5cf6" },
    },
  });

  const [providers, setProviders] = useState([]);
  const [connectedApps, setConnectedApps] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [connectionError, setConnectionError] = useState(null);

  const [selectedSlug, setSelectedSlug] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [instructions, setInstructions] = useState("");

  const loadConnectedApps = useCallback(async () => {
    setLoadingConnections(true);
    setConnectionError(null);

    try {
      const response = await composioService.getConnectedApps();
      const connections = response?.connections || response?.connected_apps || [];

      // Only apps with a live connection can be offered as a source.
      const active = [];
      const seen = new Set();
      connections.forEach((connection) => {
        const status = connection.status || connection.connection_status;
        if (status !== "active" && status !== "connected") return;

        const slug = (
          connection.toolkit_slug ||
          connection.app_name ||
          connection.name ||
          connection.app_id ||
          ""
        )
          .toString()
          .toLowerCase();
        if (!slug || seen.has(slug)) return;
        seen.add(slug);

        active.push({
          slug,
          appName: connection.toolkit_name || connection.app_name || connection.name || slug,
          appId: connection.app_id ?? connection.appId ?? "",
        });
      });

      setConnectedApps(active);
    } catch (error) {
      console.error("Failed to load connected apps:", error);
      setConnectionError("Could not load your connected apps. Try again in a moment.");
      setConnectedApps([]);
    } finally {
      setLoadingConnections(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setProviders(Array.isArray(initialProviders) ? initialProviders.map((p) => ({ ...p })) : []);
    setSelectedSlug("");
    setSourceName("");
    setInstructions("");
    loadConnectedApps();
  }, [open, initialProviders, loadConnectedApps]);

  const usedSlugs = useMemo(
    () => new Set(providers.map((provider) => provider.toolkitSlug)),
    [providers]
  );

  const availableApps = useMemo(
    () => connectedApps.filter((app) => !usedSlugs.has(app.slug)),
    [connectedApps, usedSlugs]
  );

  const handleAdd = () => {
    const app = connectedApps.find((candidate) => candidate.slug === selectedSlug);
    if (!app) return;

    setProviders((prev) => [
      ...prev,
      {
        id: `${app.slug}-${Date.now()}`,
        toolkitSlug: app.slug,
        appName: app.appName,
        appId: app.appId ? String(app.appId) : "",
        name: sourceName.trim() || app.appName,
        mode: "agent",
        instructions: instructions.trim(),
      },
    ]);

    setSelectedSlug("");
    setSourceName("");
    setInstructions("");
  };

  const handleRemove = (id) => {
    setProviders((prev) => prev.filter((provider) => provider.id !== id));
  };

  const handleFieldChange = (id, field, value) => {
    setProviders((prev) =>
      prev.map((provider) => (provider.id === id ? { ...provider, [field]: value } : provider))
    );
  };

  const handleSave = () => {
    onSave(providers);
    onClose(providers);
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog
        open={open}
        onClose={() => onClose(null)}
        maxWidth={false}
        fullWidth
        scroll="paper"
        className={styles.dialog}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(24, 24, 27, 0.4)",
              backdropFilter: "blur(2px)",
            },
          },
        }}
        PaperProps={{ className: styles.dialogPaper }}
      >
        <DialogTitle className={styles.dialogTitle} component="div">
          <Box className={styles.titleRow}>
            <Typography component="h3" variant="h6" className={styles.title}>
              Configure Live Sources
            </Typography>
          </Box>
          <IconButton
            onClick={() => onClose(null)}
            size="small"
            className={styles.closeButton}
            aria-label="Close"
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          <Typography variant="body2" className={styles.introText}>
            A live source lets this agent <strong>look things up</strong> in a connected app while it
            answers, instead of you uploading files. That is different from a tool, which lets the
            agent <strong>do</strong> things in an app — the same app can be both.
          </Typography>

          {connectionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {connectionError}
            </Alert>
          )}

          {!loadingConnections && connectedApps.length === 0 && !connectionError && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You have no connected apps yet. Connect one from Configure → Tools, then come back
              here.
            </Alert>
          )}

          <Box className={styles.addRow}>
            <FormControl size="small" fullWidth disabled={loadingConnections || availableApps.length === 0}>
              <InputLabel id="live-source-app-label">Connected app</InputLabel>
              <Select
                labelId="live-source-app-label"
                label="Connected app"
                value={selectedSlug}
                onChange={(e) => setSelectedSlug(e.target.value)}
              >
                {availableApps.map((app) => (
                  <MenuItem key={app.slug} value={app.slug}>
                    {app.appName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              fullWidth
              label="Label (optional)"
              placeholder="Company Drive"
              value={sourceName}
              onChange={(e) => setSourceName(e.target.value)}
              helperText="Shown to you and to the model"
            />

            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={!selectedSlug}
              className={styles.addButton}
            >
              Add
            </Button>
          </Box>

          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="Lookup instructions (optional)"
            placeholder="Prefer the 2026 folder."
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            sx={{ mt: 1.5 }}
          />

          {loadingConnections && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading your connected apps...
              </Typography>
            </Box>
          )}

          <Box className={styles.providerList}>
            {providers.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No live sources yet.
              </Typography>
            ) : (
              providers.map((provider) => {
                const isDisconnected = !connectedApps.some(
                  (app) => app.slug === provider.toolkitSlug
                );

                return (
                  <Box key={provider.id} className={styles.providerCard}>
                    <Box className={styles.providerHeader}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Label"
                        value={provider.name}
                        onChange={(e) => handleFieldChange(provider.id, "name", e.target.value)}
                      />
                      <IconButton
                        size="small"
                        aria-label={`Remove ${provider.name}`}
                        onClick={() => handleRemove(provider.id)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Box>

                    <Box className={styles.providerMeta}>
                      <Chip size="small" variant="outlined" label={provider.appName} />
                      {/* A disconnected app is not an error: the agent emits a
                          tool_warning and reconnecting fixes it with no re-config. */}
                      {isDisconnected && !loadingConnections && (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label="Not connected right now"
                        />
                      )}
                    </Box>

                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      label="Lookup instructions (optional)"
                      value={provider.instructions}
                      onChange={(e) =>
                        handleFieldChange(provider.id, "instructions", e.target.value)
                      }
                    />
                  </Box>
                );
              })
            )}
          </Box>
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => onClose(null)} variant="text" className={styles.cancelButton}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" className={styles.saveButton}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default LiveSourcesDialog;
